import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type {
    CategorizedGroup,
    FilterCategories,
    FilterCategory,
    IFilterCategoryWithCount,
    IFiltersConfigCategoryItem,
} from "../../types/category";
import type { FilterVisibility } from "../../types/filters";

import type { CategorizableFilter, CategorizedGroupsResult, ICategoriesService } from "./types";

type CategorizableFilterCore = {
    readonly visibility?: FilterVisibility;
    readonly category?: FilterCategories;
};

export const createCategoriesService = (): ICategoriesService => {
    const isPermanent = (filter: CategorizableFilterCore): boolean => filter.visibility === "permanent";

    const getPrimaryCategory = (filter: CategorizableFilterCore): FilterCategory | undefined => {
        if (isPermanent(filter) || !filter.category) {
            return undefined;
        }

        return is.readonlyArray(filter.category) ? filter.category[0] : filter.category;
    };

    const groupFiltersByCategory = <F extends CategorizableFilter>(
        filtersList: readonly F[],
        categories: readonly IFiltersConfigCategoryItem[],
        isSearchActive = false,
    ): CategorizedGroupsResult<F> => {
        const permanentFilters: F[] = [];
        const categoryFiltersMap = new Map<FilterCategory, F[]>();

        for (const filter of filtersList) {
            if (isPermanent(filter)) {
                permanentFilters.push(filter);

                continue;
            }

            const primaryCategory = getPrimaryCategory(filter);

            if (!primaryCategory) {
                continue;
            }

            const existing = categoryFiltersMap.get(primaryCategory);

            if (existing) {
                existing.push(filter);
            } else {
                categoryFiltersMap.set(primaryCategory, [filter]);
            }
        }

        const groups: CategorizedGroup<F>[] = [];

        for (const categoryConfig of categories) {
            const filters = categoryFiltersMap.get(categoryConfig.id) ?? [];

            const canShowEmptyContent = Boolean(categoryConfig.emptyContent) && !isSearchActive;

            if (filters.length > 0 || canShowEmptyContent) {
                groups.push({ category: categoryConfig, filters });
            }
        }

        return { permanentFilters, groups };
    };

    const computeCategoryCounts = <F extends CategorizableFilter>(
        filtersList: readonly F[],
        categories: readonly IFiltersConfigCategoryItem[],
    ): readonly IFilterCategoryWithCount[] => {
        const countMap = new Map<FilterCategory | null, number>();

        filtersList.forEach((filter) => {
            const cat = getPrimaryCategory(filter);

            if (cat) {
                countMap.set(cat, (countMap.get(cat) ?? 0) + 1);
            }
        });

        return categories.map((cat) => {
            const categoryCount = countMap.get(cat.id) ?? 0;

            return {
                ...cat,
                count: cat.resolveCount?.(filtersList.length, categoryCount) ?? categoryCount,
            };
        });
    };

    const getCategoriesWithHiddenFilters = <F extends CategorizableFilter>({
        filtersList,
        isFilterHidden,
    }: {
        readonly filtersList: readonly F[];
        readonly isFilterHidden: (filter: F) => boolean;
    }): ReadonlySet<FilterCategory> => {
        const groups = new Map<FilterCategory, F[]>();

        for (const filter of filtersList) {
            const primaryCategory = getPrimaryCategory(filter);

            if (!primaryCategory) {
                continue;
            }

            const bucket = groups.get(primaryCategory);

            if (bucket) {
                bucket.push(filter);
            } else {
                groups.set(primaryCategory, [filter]);
            }
        }

        const result = new Set<FilterCategory>();

        for (const [category, bucket] of groups) {
            if (bucket.length > 1 && bucket.some(isFilterHidden)) {
                result.add(category);
            }
        }

        return result;
    };

    return {
        groupFiltersByCategory,
        computeCategoryCounts,
        getCategoriesWithHiddenFilters,
    };
};
